// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { ColorKey, IconColor } from '@iheartjane/reefer';
import { getColorCSSVar } from '@iheartjane/reefer';
import { transparentize } from 'polished';
import { useEffect, useRef } from 'react';

import { useKioskSpacing } from '@jane/kiosk/hooks';
import { useComplianceLanguage } from '@jane/kiosk/providers';
import type { MenuCategory as TMenuCategory } from '@jane/kiosk/util';
import { capitalize, customCategoryLabel } from '@jane/kiosk/util';
import { truncateLabel } from '@jane/shared-ecomm/util';
import type { Store } from '@jane/shared/types';

import { LabeledIconButton } from '../IconButton/IconButton';
import type { CategoryMeta } from './categories';

interface MenuCategoryProps {
  category: TMenuCategory;
  handleCategorySelected(category: TMenuCategory): void;
  isJaneMode?: boolean;
  meta: CategoryMeta;
  selected: boolean;
  store: Store;
}

const StyledMenuCategory = styled.div<{
  iconColor: ColorKey;
  isJaneMode?: boolean;
  isMono: boolean;
  isSelected: boolean;
}>(({ iconColor, isJaneMode, isMono, isSelected, theme }) => ({
  // themed menu category styles
  ...(isMono &&
    !isJaneMode && {
      'path.Details': {
        fill: isSelected ? theme.colors.text.info : theme.colors.primary.main,
        fillOpacity: 1,
      },
      'ellipse.Details': {
        fill: isSelected ? theme.colors.text.info : theme.colors.primary.main,
        fillOpacity: 1,
      },
      'path.Mono-Details': {
        fill: isSelected ? theme.colors.text.inverse : theme.colors.text.info,
        fillOpacity: 1,
      },
    }),
  // jane mode menu category styles
  ...(isJaneMode &&
    isSelected && {
      'path.Details': {
        fill: getColorCSSVar(iconColor),
        fillOpacity: 1,
      },
      'ellipse.Details': {
        fill: getColorCSSVar(iconColor),
        fillOpacity: 1,
      },
      'path.Mono-Details': {
        fill: theme.colors.grays.white,
        fillOpacity: 1,
      },
    }),
}));

export const MenuCategory = ({
  category,
  isJaneMode,
  handleCategorySelected,
  meta,
  selected,
  store,
}: MenuCategoryProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const { isIpad } = useKioskSpacing();
  const theme = useTheme();
  const { getWord } = useComplianceLanguage();

  const { color, colorKey, selectedColor, Icon } = meta;

  const isSpecials = category === 'specials';
  const isGold = category === 'jane-gold';

  const compliantLabel = isSpecials ? capitalize(getWord('deals')) : null;

  let backgroundColor = selected
    ? theme.colors.text.info
    : transparentize(0.9, theme.colors.text.info);
  let iconColor: IconColor = selected
    ? isGold
      ? 'gold'
      : 'text-inverse'
    : 'text-info';
  if (isJaneMode) {
    backgroundColor = selected ? selectedColor : color;
    iconColor = selected
      ? 'grays-white'
      : category !== 'all'
        ? colorKey
        : undefined;
  }

  const productKindLabel = customCategoryLabel({
    kind: category,
    store,
  });

  const categoryLabel = compliantLabel || productKindLabel;

  useEffect(
    () => {
      if (selected) {
        divRef.current?.scrollIntoView?.({
          behavior: 'auto',
          block: 'center',
          inline: 'center',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [/** run only on mount: when returning from cart page  */]
  );

  const monoIcon = !isJaneMode || selected;

  return (
    <StyledMenuCategory
      ref={divRef}
      isMono={monoIcon}
      isSelected={selected}
      isJaneMode={isJaneMode}
      iconColor={colorKey}
    >
      <LabeledIconButton
        ariaLabel={categoryLabel}
        backgroundColor={backgroundColor}
        icon={<Icon color={iconColor} size={isIpad ? 'lg' : 'xl'} />}
        labelColor={isJaneMode ? 'text-main' : 'text-info'}
        large={!isIpad}
        onClick={() => handleCategorySelected(category)}
        selected={selected}
        title={truncateLabel(categoryLabel, 12)}
      />
    </StyledMenuCategory>
  );
};
